import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaSearch, FaTimes } from "react-icons/fa";
import home1 from "../../images/icon_home.svg";
import home from "../../images/home.png";
import { useTranslation } from 'react-i18next';
import { useLanguageData } from '../../hooks/useLanguageData';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import SearchModal from './SearchModal';

function Navbar() {
    const location = useLocation();
    const [zoomLevel, setZoomLevel] = useState(1);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { t, i18n } = useTranslation();
    const [menuItems, setMenuItems] = useState([]);
    
    const searchInputRef = useRef(null);
    const [isHomeHovered, setIsHomeHovered] = useState(false);

    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // État pour reCAPTCHA
    const [recaptchaToken, setRecaptchaToken] = useState('');
    const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
    const recaptchaRef = useRef(null);
    const isMountedRef = useRef(true); // ✅ Ajout du ref pour suivre le montage
    
    const [appointmentData, setAppointmentData] = useState({
        nom: '', 
        prenom: '', 
        email: '', 
        phone: '',
        message: '', 
        objet: '',
        autresPreoccupation: '',
        pays: ''
    });

    // Détection responsive améliorée
    const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 1100);

    // Récupérer la clé reCAPTCHA depuis .env
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    // Vérification stricte
    if (!recaptchaSiteKey) {
        console.error('❌ REACT_APP_RECAPTCHA_SITE_KEY n\'est pas définie dans le fichier .env');
    }

    // Récupérer les résultats de recherche
    const { 
        data: searchData, 
        loading: searchLoading, 
        error: searchError 
    } = useLanguageData(searchKeyword ? `/api/v1/embassy/search?keywords=${encodeURIComponent(searchKeyword)}` : null);

    // ✅ Initialisation de reCAPTCHA - VERSION CORRIGÉE
    useEffect(() => {
        // Fonctions globales pour reCAPTCHA
        const handleSuccess = (token) => {
            if (isMountedRef.current) {
                setRecaptchaToken(token);
            }
        };

        const handleExpired = () => {
            if (isMountedRef.current) {
                setRecaptchaToken('');
            }
        };

        const handleError = () => {
            if (isMountedRef.current) {
                setRecaptchaToken('');
            }
        };

        window.handleRecaptchaSuccess = handleSuccess;
        window.handleRecaptchaExpired = handleExpired;
        window.handleRecaptchaError = handleError;

        // Vérifier si reCAPTCHA est déjà chargé
        if (window.grecaptcha) {
            setIsRecaptchaLoaded(true);
            return;
        }

        // Charger le script reCAPTCHA
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
        script.async = true;
        script.defer = true;
        
        // Fonction globale appelée quand reCAPTCHA est chargé
        window.onRecaptchaLoad = () => {
            if (isMountedRef.current) {
                setIsRecaptchaLoaded(true);
                
                // Si le modal est ouvert, rendre explicitement reCAPTCHA
                if (showAppointmentModal && recaptchaRef.current) {
                    try {
                        window.grecaptcha.render(recaptchaRef.current, {
                            sitekey: recaptchaSiteKey,
                            callback: 'handleRecaptchaSuccess',
                            'expired-callback': 'handleRecaptchaExpired',
                            'error-callback': 'handleRecaptchaError'
                        });
                    } catch (error) {
                        console.error('Erreur lors du rendu reCAPTCHA:', error);
                    }
                }
            }
        };
        
        document.head.appendChild(script);

        return () => {
            isMountedRef.current = false;
            // ❌ NE PAS SUPPRIMER onRecaptchaLoad - c'est la cause de l'erreur
            // delete window.onRecaptchaLoad;
            
            // ✅ Nettoyer uniquement nos propres fonctions
            if (window.handleRecaptchaSuccess === handleSuccess) {
                window.handleRecaptchaSuccess = undefined;
            }
            if (window.handleRecaptchaExpired === handleExpired) {
                window.handleRecaptchaExpired = undefined;
            }
            if (window.handleRecaptchaError === handleError) {
                window.handleRecaptchaError = undefined;
            }
        };
    }, [recaptchaSiteKey, showAppointmentModal]);

    // ✅ Rendre reCAPTCHA quand le modal s'ouvre - VERSION CORRIGÉE
    useEffect(() => {
        if (showAppointmentModal && isRecaptchaLoaded && window.grecaptcha && recaptchaRef.current && isMountedRef.current) {
            try {
                // Nettoyer l'ancienne instance si elle existe
                if (recaptchaRef.current.firstChild) {
                    window.grecaptcha.reset();
                } else {
                    window.grecaptcha.render(recaptchaRef.current, {
                        sitekey: recaptchaSiteKey,
                        callback: 'handleRecaptchaSuccess',
                        'expired-callback': 'handleRecaptchaExpired',
                        'error-callback': 'handleRecaptchaError'
                    });
                }
            } catch (error) {
                console.error('Erreur lors du rendu reCAPTCHA dans le modal:', error);
            }
        }
    }, [showAppointmentModal, isRecaptchaLoaded, recaptchaSiteKey]);

    // Options pour le select objet
    const objetOptions = [
        { value: "RAVIP", label: "RAVIP" },
        { value: "Carte consulaire", label: "Carte consulaire" },
        { value: "Passeport", label: "Passeport" },
        { value: "Légalisation", label: "Légalisation" },
        { value: "Certification", label: "Certification" },
        { value: "Mariage", label: "Mariage" },
        { value: "Autres préoccupations", label: "Autres préoccupations", hasInput: true }
    ];

    // Options pour le select pays
    const paysOptions = [
        { value: "Qatar", label: "Qatar" },
        { value: "Iran", label: "Iran" },
        { value: "Irak", label: "Irak" },
        { value: "Afghanistan", label: "Afghanistan" },
        { value: "Pakistan", label: "Pakistan" },
        { value: "Syrie", label: "Syrie" }
    ];

    // Récupérer les menus depuis l'API
    const { 
        data: menuResponse, 
        loading: menuLoading 
    } = useLanguageData('/api/v1/embassy/useful-links-category?slugCategory=links-nav-usa');

    // Récupérer l'URL de base depuis .env
    const baseUrl = process.env.REACT_APP_BASE_URL;

    // Fonction pour extraire le numéro du slug
    const extractNumberFromSlug = (slug) => {
        const match = slug.match(/(\d+)/);
        return match ? parseInt(match[1], 10) : 0;
    };

    // Fonction de recherche
    const performSearch = async (query) => {
        setSearchKeyword(query);
    };

    // Construire la structure des menus
    useEffect(() => {
        if (menuResponse?.data && Array.isArray(menuResponse.data)) {
            const sortedData = [...menuResponse.data].sort((a, b) => {
                const numA = extractNumberFromSlug(a.slug);
                const numB = extractNumberFromSlug(b.slug);
                return numA - numB;
            });
            
            const menuGroups = {};
            
            sortedData.forEach(item => {
                const slug = item.slug;
                
                if (slug.startsWith('menu')) {
                    if (!menuGroups[slug]) {
                        menuGroups[slug] = {
                            main: item,
                            subMenus: []
                        };
                    } else {
                        menuGroups[slug].main = item;
                    }
                } else if (slug.startsWith('submenu')) {
                    const match = slug.match(/submenu(\d+)_menu(\d+)/);
                    if (match) {
                        const menuNumber = match[2];
                        const menuKey = `menu${menuNumber}`;
                        
                        if (!menuGroups[menuKey]) {
                            menuGroups[menuKey] = {
                                main: null,
                                subMenus: []
                            };
                        }
                        menuGroups[menuKey].subMenus.push(item);
                    }
                }
            });
            
            Object.keys(menuGroups).forEach(key => {
                if (menuGroups[key].subMenus.length > 0) {
                    menuGroups[key].subMenus.sort((a, b) => {
                        const numA = extractNumberFromSlug(a.slug);
                        const numB = extractNumberFromSlug(b.slug);
                        return numA - numB;
                    });
                }
            });
            
            const menuItemsArray = [
                {
                    label: <img
                        src={isHomeHovered || location.pathname === "/" ? home1 : home}
                        alt="home"
                        style={{ width: "31px" }}
                    />,
                    path: "/",
                    hasHover: true,
                    isIcon: true,
                    onMouseEnter: () => setIsHomeHovered(true),
                    onMouseLeave: () => setIsHomeHovered(false)
                }
            ];
            
            const menuOrder = ['menu1', 'menu2', 'menu3', 'menu4', 'menu5', 'menu6', 'menu7', 'menu8'];
            
            menuOrder.forEach(menuKey => {
                const group = menuGroups[menuKey];
                if (group && group.main) {
                    const menuItem = {
                        label: group.main.name,
                        path: group.main.linkAccess || "#",
                        hasHover: true
                    };
                    
                    if (group.subMenus.length > 0) {
                        menuItem.subMenu = group.subMenus.map(sub => ({
                            label: sub.name,
                            path: sub.linkAccess || "#"
                        }));
                    }
                    
                    if (group.main.linkAccess && group.main.linkAccess.startsWith('http')) {
                        menuItem.external = true;
                    }
                    
                    if (menuKey === 'menu8') {
                        menuItem.special = true;
                    }
                    
                    menuItemsArray.push(menuItem);
                }
            });
            
            menuItemsArray.push({
                label: <FaSearch />,
                path: "#",
                hasHover: true,
                isIcon: true,
                onClick: () => setShowSearchModal(true)
            });
            
            setMenuItems(menuItemsArray);
        }
    }, [menuResponse, isHomeHovered, location.pathname]);

    // Détecter le niveau de zoom
    useEffect(() => {
        const detectZoom = () => {
            let zoom = window.devicePixelRatio || 1;
            setZoomLevel(zoom);
        };

        detectZoom();
        window.addEventListener('resize', detectZoom);

        return () => {
            window.removeEventListener('resize', detectZoom);
        };
    }, []);

    useEffect(() => {
        if (showSearchModal && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [showSearchModal]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth <= 1100;
            setIsMobileView(mobile);
            if (!mobile) {
                setIsMobileMenuOpen(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleCloseModal = () => {
        setShowSearchModal(false);
        setShowAppointmentModal(false);
        setSearchKeyword('');
        setRecaptchaToken('');
        setAppointmentData({ 
            nom: '', 
            prenom: '', 
            email: '', 
            phone: '', 
            message: '', 
            objet: '',
            autresPreoccupation: '',
            pays: ''
        });
        // Réinitialiser reCAPTCHA
        if (window.grecaptcha) {
            try {
                window.grecaptcha.reset();
            } catch (error) {
                console.error('Erreur lors de la réinitialisation reCAPTCHA:', error);
            }
        }
    };

    const handleAppointmentClick = (e) => {
        e.preventDefault();
        setShowAppointmentModal(true);
    };

    const handleAppointmentSubmit = async (e) => {
        e.preventDefault();
        
        // Vérification reCAPTCHA
        if (!recaptchaToken) {
            alert("Veuillez valider le reCAPTCHA");
            return;
        }
        
        let objetFinal = appointmentData.objet;
        if (appointmentData.objet === "Autres préoccupations") {
            if (!appointmentData.autresPreoccupation.trim()) {
                alert("Veuillez préciser votre préoccupation");
                return;
            }
            objetFinal = `Autres préoccupations: ${appointmentData.autresPreoccupation}`;
        }
        
        if (!appointmentData.nom || !appointmentData.prenom || !appointmentData.email || !appointmentData.phone || !appointmentData.objet || !appointmentData.pays) {
            alert("Veuillez remplir tous les champs obligatoires");
            return;
        }
        
        setIsSubmitting(true);
        
        try {
            const formPayload = {
                "code-ambassade": "uuidARBDA05",
                "lastname": appointmentData.nom,
                "firstname": appointmentData.prenom,
                "email": appointmentData.email,
                "phone": appointmentData.phone,
                "object": objetFinal,
                "content": appointmentData.message || "Demande de rendez-vous",
                "town": "Benin",
                "themeDiscuss": "",
                "country": appointmentData.pays,
                "acceptDataTreat": true
            };
            
            const response = await fetch(`${baseUrl}/api/v1/embassy/message`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(formPayload)
            });
            
            const result = await response.json();
            
            if (response.ok && result.response_code === 200) {
                alert(result.message || "Votre demande de rendez-vous a été envoyée avec succès !");
                handleCloseModal();
            } else {
                throw new Error(result.message || "Erreur lors de l'envoi de la demande");
            }
            
        } catch (error) {
            console.error('Erreur lors de l\'envoi:', error);
            alert(error.message || "Une erreur est survenue. Veuillez réessayer plus tard.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleBackdropClick = (e) => { 
        if (e.target === e.currentTarget) handleCloseModal(); 
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setAppointmentData(prev => ({ ...prev, [name]: value }));
    };

    const handlePhoneChange = (value) => {
        setAppointmentData(prev => ({ ...prev, phone: value }));
    };

    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    const isActivePath = (path) => {
        if (path === "/") {
            return location.pathname === "/";
        }
        if (path === "/Articles") {
            return location.pathname.startsWith("/Articles") ||
                   location.pathname.startsWith("/article/");
        }
        if (path === "/Cooperation-bilaterale") {
            return location.pathname.startsWith("/Cooperation-bilaterale");
        }
        if (path === "/Consulat-honoraires") {
            return location.pathname === "/Consulat-honoraires";
        }
        return location.pathname.startsWith(path);
    };

    const activeSubMenuIndex = menuItems.findIndex(item =>
        item.subMenu && item.subMenu.some(sub => {
            if (!sub.external) {
                if (sub.path === "/Articles") {
                    return location.pathname.startsWith("/Articles") ||
                        location.pathname.startsWith("/article/");
                }
                return isActivePath(sub.path);
            }
            return false;
        })
    );

    const isSubMenuItemActive = (subItem) => {
        if (subItem.external) return false;
        if (subItem.path === "/Consulat-honoraires") {
            return location.pathname === "/Consulat-honoraires";
        }
        if (subItem.path === "/Cooperation-bilaterale") {
            return location.pathname.startsWith("/Cooperation-bilaterale");
        }
        return isActivePath(subItem.path);
    };

    // Si chargement, afficher un spinner
    if (menuLoading) {
        return (
            <div className="navbar-ultimate-container">
                <div className="navbar-ultimate-inner">
                    <div style={{ padding: '10px' }}>
                        <div style={{
                            width: "30px",
                            height: "30px",
                            border: "3px solid #f3f3f3",
                            borderTop: "3px solid #1a3c5e",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite"
                        }}></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <style type="text/css">
                {`
                    /* GLOBAL & ANTI-ZOOM AVEC RESPONSIVITÉ */
                    .navbar-ultimate-container {
                        width: 100%; 
                        display: flex; 
                        justify-content: center;
                        background-color: #f1f0f0ff; 
                        position: relative; 
                        z-index: 1000;
                        height: clamp(50px, 3.1vw, 60px);
                        border-bottom: 1px solid #e0e0e0;
                    }
                    
                    .navbar-ultimate-inner { 
                        display: flex; 
                        justify-content: center; 
                        align-items: center; 
                        width: 100%; 
                        height: 100%; 
                    }
                    
                    .navbar-nav-ultimate { 
                        display: flex; 
                        align-items: center; 
                        list-style: none; 
                        padding: 0; 
                        margin: 0; 
                        height: 100%; 
                    }
                    
                    .nav-item-ultimate { 
                        height: 100%; 
                        display: flex; 
                        align-items: center; 
                    }

                    .nav-link-ultimate {
                        display: flex; 
                        align-items: center; 
                        text-decoration: none !important;
                        white-space: nowrap; 
                        text-transform: uppercase; 
                        font-family: 'Montserrat', sans-serif;
                        font-weight: 600; 
                        height: 100%; 
                        transition: all 0.2s ease;
                        border: none; 
                        background: none; 
                        cursor: pointer; 
                        color: #0a2c57;
                        font-size: clamp(12px, 0.9vw, 16px) !important; 
                        padding: 0 clamp(8px, 3vw, 20px) !important;
                    }

                    .nav-link-ultimate:hover { 
                        background-color: #1880e7; 
                        color: white !important; 
                    }
                    
                    .nav-link-ultimate.active-link { 
                        background-color: #0a3764; 
                        color: white !important; 
                    }
                    
                    .nav-link-ultimate.special-btn { 
                        background-color: #de2910 !important; 
                        color: white !important; 
                    }
                    
                    .nav-link-ultimate.special-btn:hover { 
                        background-color: #de2910 !important; 
                    }

                    .submenu-ultimate-container {
                        width: 100%;  
                        display: flex; 
                        justify-content: center;
                        height: clamp(35px, 3.1vw, 60px);
                    }
                    
                    .submenu-nav-ultimate { 
                        display: flex; 
                        background-color: #e9f0f9;
                        align-items: center; 
                        justify-content: center; 
                        height: 100%; 
                        padding: 0; 
                        margin: 0; 
                    }
                    
                    .submenu-item-ultimate { 
                        height: 100%; 
                        display: flex; 
                        align-items: center; 
                    }
                    
                    .submenu-link-ultimate {
                        display: flex; 
                        align-items: center; 
                        height: 100%; 
                        text-decoration: none !important;
                        text-transform: uppercase; 
                        font-weight: 500; 
                        color: #0a2c57 !important;
                        position: relative; 
                        transition: all 0.2s;
                        font-size: clamp(11px, 0.9vw, 14px) !important;
                        padding: 0 clamp(10px, 7.9vw, 55vw) !important;
                    }
                    
                    .submenu-link-ultimate:hover { 
                        background-color: white; 
                    }
                    
                    .submenu-link-active { 
                        background-color: white; 
                        font-weight: 700 !important; 
                        border-bottom: 3px solid #de2910 !important; 
                    }

                    .hamburger {
                        display: none;
                        position: absolute; 
                        top: 50%;
                        transform: translateY(-50%);
                        right: 20px; 
                        z-index: 1001;
                        flex-direction: column; 
                        cursor: pointer; 
                        padding: 10px;
                        background: none;
                        border: none;
                    }

                    .bar { 
                        width: 25px; 
                        height: 3px; 
                        background-color: #0a2c57; 
                        margin: 3px 0; 
                        transition: all 0.3s ease-in-out; 
                        border-radius: 2px; 
                        transform-origin: 3px 1px; 
                    }

                    .bar.open:nth-child(1) { transform: rotate(45deg); }
                    .bar.open:nth-child(2) { opacity: 0; transform: translateX(-20px); }
                    .bar.open:nth-child(3) { transform: rotate(-45deg); }

                    /* MODAL RENDEZ-VOUS AVEC SCROLL ET reCAPTCHA */
                    .appointment-modal-backdrop {
                        position: fixed;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background-color: rgba(10, 43, 87, 0.85);
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        z-index: 2000;
                        padding: 20px;
                    }

                    .appointment-modal-content {
                        background: #e7ebf0;
                        width: 90%;
                        max-width: 650px;
                        max-height: 85vh;
                        border-radius: 12px;
                        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
                        display: flex;
                        flex-direction: column;
                        overflow: hidden;
                    }

                    .appointment-modal-header {
                        padding: 1rem 1.5rem;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        border-bottom: 1px solid #d0d0d0;
                        flex-shrink: 0;
                        background: #e7ebf0;
                    }

                    .appointment-modal-title {
                        margin: 0;
                        font-size: 1.5rem;
                        font-weight: 700;
                        color: #0a2c57;
                    }

                    .appointment-modal-close {
                        background: none;
                        border: 1px solid #0a2c57;
                        color: #0a2c57;
                        font-size: 1.2rem;
                        cursor: pointer;
                        width: 40px;
                        height: 40px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 8px;
                        transition: all 0.3s ease;
                    }

                    .appointment-modal-close:hover {
                        background-color: rgba(10, 44, 87, 0.1);
                    }

                    .appointment-modal-body {
                        padding: 1.5rem;
                        overflow-y: auto;
                        flex: 1;
                    }

                    .appointment-modal-body::-webkit-scrollbar {
                        width: 6px;
                    }

                    .appointment-modal-body::-webkit-scrollbar-track {
                        background: #e0e0e0;
                        border-radius: 10px;
                    }

                    .appointment-modal-body::-webkit-scrollbar-thumb {
                        background: #de2910;
                        border-radius: 10px;
                    }

                    .appointment-modal-body::-webkit-scrollbar-thumb:hover {
                        background: #b8220d;
                    }

                    .appointment-form {
                        display: flex;
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .form-group {
                        display: flex;
                        flex-direction: column;
                    }

                    .form-row {
                        display: flex;
                        gap: 1rem;
                        flex-wrap: wrap;
                    }

                    .form-row .form-group {
                        flex: 1;
                        min-width: 180px;
                    }

                    .form-input, .form-select, .form-textarea {
                        padding: 0.75rem;
                        border: 1px solid #ddd;
                        border-radius: 8px;
                        font-size: 0.95rem;
                        font-family: 'Montserrat', sans-serif;
                        background-color: white;
                        transition: border-color 0.3s ease;
                    }

                    .form-input:focus, .form-select:focus, .form-textarea:focus {
                        outline: none;
                        border-color: #0a2c57;
                    }

                    .form-textarea {
                        min-height: 100px;
                        resize: vertical;
                    }

                    .phone-input-wrapper {
                        width: 100%;
                    }

                    .phone-input-wrapper .react-tel-input {
                        width: 100%;
                    }

                    .phone-input-wrapper .react-tel-input .form-control {
                        width: 100% !important;
                        height: auto !important;
                        padding: 0.75rem 0.75rem 0.75rem 52px !important;
                        font-size: 0.95rem !important;
                        border: 1px solid #ddd !important;
                        border-radius: 8px !important;
                        font-family: 'Montserrat', sans-serif !important;
                        background-color: white !important;
                    }

                    .phone-input-wrapper .react-tel-input .form-control:focus {
                        outline: none !important;
                        border-color: #0a2c57 !important;
                        box-shadow: none !important;
                    }

                    .phone-input-wrapper .react-tel-input .flag-dropdown {
                        background-color: white !important;
                        border: 1px solid #ddd !important;
                        border-right: none !important;
                        border-radius: 8px 0 0 8px !important;
                    }

                    .recaptcha-container {
                        display: flex;
                        margin: 0.5rem 0;
                        min-height: 80px;
                    }

                    .recaptcha-loading {
                        text-align: center;
                        padding: 1rem;
                        color: #666;
                        font-size: 0.85rem;
                        background: #f5f5f5;
                        border-radius: 8px;
                    }

                    .appointment-submit-btn {
                        background-color: #de2910 !important;
                        color: white;
                        border: none;
                        padding: 0.75rem 1.5rem;
                        border-radius: 8px;
                        font-size: 1rem;
                        font-weight: 600;
                        cursor: pointer;
                        transition: background-color 0.3s ease;
                        margin-top: 0.5rem;
                    }

                    .appointment-submit-btn:hover:not(:disabled) {
                        background-color: #b8220d !important;
                    }

                    .appointment-submit-btn:disabled {
                        background-color: #cccccc;
                        cursor: not-allowed;
                    }

                    .phone-input-wrapper .react-tel-input .country-list {
                        width: 280px !important;
                        max-height: 250px !important;
                        z-index: 1000 !important;
                        text-align: left !important;
                    }

                    .navbar-nav-ultimate.active-mobile {
                        position: absolute; 
                        top: 100%; 
                        left: 0; 
                        right: 0; 
                        background-color: white;
                        flex-direction: column; 
                        height: auto;
                        width: 100%; 
                        z-index: 100; 
                        display: flex !important;
                        border-top: 1px solid #ddd;
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    }

                    .nav-item-ultimate.mobile {
                        width: 100%; 
                        height: 50px; 
                        border-bottom: 1px solid #f1f1f1;
                        justify-content: flex-start;
                    }

                    .nav-link-ultimate.mobile {
                        width: 100%;
                        height: 100%;
                        font-size: 14px !important;
                        padding: 0 20px !important;
                        justify-content: flex-start;
                    }

                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }

                    @media (max-width: 1400px) and (min-width: 1101px) {
                        .nav-link-ultimate {
                            font-size: clamp(10px, 0.82vw, 14px) !important;
                            padding: 0 clamp(8px, 3.2vw, 20.5px) !important;
                        }
                        
                        .submenu-link-ultimate {
                            font-size: clamp(9px, 0.82vw, 12px) !important;
                            padding: 0 clamp(8px, 7vw, 55vw) !important;
                        }
                        
                        .navbar-ultimate-container {
                            height: clamp(40px, 3.1vw, 60px)!important;
                        }
                    }

                    @media (min-width: 2000px) {
                        .nav-link-ultimate {
                            font-size: 17px !important;
                            padding: 0 35px !important;
                        }
                        
                        .submenu-link-ultimate {
                            font-size: clamp(9px, 0.7vw, 15px) !important;
                            padding: 0 clamp(8px, 2.5vw, 55vw) !important;
                        }
                    }

                    @media (max-width: 1100px) {
                        .hamburger {
                            display: flex;
                        }
                        
                        .navbar-ultimate-container {
                            justify-content: space-between;
                            padding: 0 20px;
                        }
                        
                        .navbar-ultimate-inner {
                            justify-content: flex-start;
                        }
                    }

                    @media (max-width: 1024px) {
                        .navbar-ultimate-container {
                            display: none !important;
                        }
                        
                        .submenu-ultimate-container {
                            display: none !important;
                        }
                        
                        .appointment-modal-content {
                            max-width: 95%;
                            max-height: 90vh;
                        }
                        
                        .appointment-modal-header {
                            padding: 0.8rem 1rem;
                        }
                        
                        .appointment-modal-title {
                            font-size: 1.2rem;
                        }
                        
                        .appointment-modal-close {
                            width: 35px;
                            height: 35px;
                            font-size: 1rem;
                        }
                        
                        .appointment-modal-body {
                            padding: 1rem;
                        }
                        
                        .form-row {
                            flex-direction: column;
                            gap: 0.8rem;
                        }
                        
                        .form-row .form-group {
                            min-width: 100%;
                        }
                    }

                    @media (max-width: 768px) {
                        .appointment-modal-content {
                            max-width: 95%;
                            max-height: 85vh;
                        }
                        
                        .appointment-modal-body {
                            padding: 1rem;
                        }
                        
                        .appointment-form {
                            gap: 0.8rem;
                        }
                        
                        .form-input, .form-select, .form-textarea {
                            padding: 0.6rem;
                            font-size: 0.9rem;
                        }
                        
                        .appointment-submit-btn {
                            padding: 0.6rem 1.2rem;
                            font-size: 0.9rem;
                        }

                        .recaptcha-container {
                            transform: scale(0.95);
                            min-height: 70px;
                        }
                    }

                    @media (max-width: 480px) {
                        .appointment-modal-header {
                            padding: 0.6rem 0.8rem;
                        }
                        
                        .appointment-modal-title {
                            font-size: 1rem;
                        }
                        
                        .appointment-modal-close {
                            width: 30px;
                            height: 30px;
                            font-size: 0.9rem;
                        }
                        
                        .appointment-modal-body {
                            padding: 0.8rem;
                        }
                        
                        .form-input, .form-select, .form-textarea {
                            padding: 0.5rem;
                            font-size: 0.85rem;
                        }
                        
                        .phone-input-wrapper .react-tel-input .form-control {
                            padding: 0.5rem 0.5rem 0.5rem 48px !important;
                            font-size: 0.85rem !important;
                        }

                        .recaptcha-container {
                            transform: scale(0.85);
                            min-height: 65px;
                        }
                    }
                `}
            </style>

            {/* Navbar principale - cachée sur mobile/tablette */}
            <div className="navbar-ultimate-container">
                <div className="navbar-ultimate-inner">
                    <button className="hamburger" onClick={toggleMobileMenu}>
                        <span className={isMobileMenuOpen ? "bar open" : "bar"}></span>
                        <span className={isMobileMenuOpen ? "bar open" : "bar"}></span>
                        <span className={isMobileMenuOpen ? "bar open" : "bar"}></span>
                    </button>
                    <nav className={`navbar-nav-ultimate ${isMobileMenuOpen ? 'active-mobile' : ''}`}>
                        {menuItems.map((item, index) => {
                            const isActive = isActivePath(item.path) || activeSubMenuIndex === index;
                            let linkClass = "nav-link-ultimate";
                            if (item.special) linkClass += " special-btn";
                            else if (isActive) linkClass += " active-link";

                            return (
                                <div className={`nav-item-ultimate ${isMobileMenuOpen ? 'mobile' : ''}`} key={index}>
                                    {item.onClick ? (
                                        <button onClick={(e) => { item.onClick(e); setIsMobileMenuOpen(false); }} className={`${linkClass} ${isMobileMenuOpen ? 'mobile' : ''}`}>
                                            {item.label}
                                        </button>
                                    ) : item.external ? (
                                        <a
                                            href={item.path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`${linkClass} ${isMobileMenuOpen ? 'mobile' : ''}`}
                                            onMouseEnter={item.onMouseEnter}
                                            onMouseLeave={item.onMouseLeave}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            {item.label}
                                        </a>
                                    ) : (
                                        <NavLink                                            to={item.path}
                                            className={`${linkClass} ${isMobileMenuOpen ? 'mobile' : ''}`}
                                            onMouseEnter={item.onMouseEnter}
                                            onMouseLeave={item.onMouseLeave}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            {item.label}
                                        </NavLink>
                                    )}
                                </div>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {/* Sous-menu - caché sur mobile/tablette */}
            {activeSubMenuIndex !== -1 && menuItems[activeSubMenuIndex].subMenu && !isMobileView && (
                <div className="submenu-ultimate-container">
                    <div className="submenu-nav-ultimate">
                        {menuItems[activeSubMenuIndex].subMenu.map((subItem, subIndex) => (
                            <div className="submenu-item-ultimate" key={subIndex}>
                                {subItem.external ? (
                                    <a
                                        href={subItem.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`submenu-link-ultimate ${isSubMenuItemActive(subItem) ? 'submenu-link-active' : ''}`}
                                    >
                                        {subItem.label}
                                    </a>
                                ) : (
                                    <NavLink
                                        to={subItem.path}
                                        className={`submenu-link-ultimate ${isSubMenuItemActive(subItem) ? 'submenu-link-active' : ''}`}
                                    >
                                        {subItem.label}
                                    </NavLink>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Modal de recherche avec SearchModal */}
            <SearchModal
                isOpen={showSearchModal}
                onClose={() => {
                    setShowSearchModal(false);
                    setSearchKeyword('');
                }}
                onSearch={performSearch}
                initialQuery=""
                isSearching={searchLoading}
                searchResults={searchData?.data}
                searchError={searchError?.message}
            />

            {/* Modal rendez-vous avec reCAPTCHA */}
            {showAppointmentModal && (
                <div className="appointment-modal-backdrop" onClick={handleBackdropClick}>
                    <div className="appointment-modal-content">
                        <div className="appointment-modal-header">
                            <h2 className="appointment-modal-title">Prendre rendez-vous</h2>
                            <button
                                className="appointment-modal-close"
                                onClick={handleCloseModal}
                                aria-label="Fermer"
                            >
                                <FaTimes />
                            </button>
                        </div>
                        <div className="appointment-modal-body">
                            <form className="appointment-form" onSubmit={handleAppointmentSubmit}>
                                <div className="form-row">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="nom"
                                            placeholder="Nom *"
                                            className="form-input"
                                            value={appointmentData.nom}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="prenom"
                                            placeholder="Prénom *"
                                            className="form-input"
                                            value={appointmentData.prenom}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <select
                                        name="objet"
                                        className="form-select"
                                        value={appointmentData.objet}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="" disabled>Sélectionnez un objet *</option>
                                        {objetOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                {appointmentData.objet === "Autres préoccupations" && (
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="autresPreoccupation"
                                            placeholder="Précisez votre préoccupation *"
                                            className="form-input"
                                            value={appointmentData.autresPreoccupation}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                )}
                                
                                <div className="form-group">
                                    <select
                                        name="pays"
                                        className="form-select"
                                        value={appointmentData.pays}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="" disabled>Sélectionnez un pays *</option>
                                        {paysOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                
                                <div className="form-group">
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email *"
                                        className="form-input"
                                        value={appointmentData.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                
                                <div className="form-group">
                                    <div className="phone-input-wrapper">
                                        <PhoneInput
                                            country={'qa'}
                                            value={appointmentData.phone}
                                            onChange={handlePhoneChange}
                                            inputProps={{
                                                name: 'phone',
                                                required: true,
                                                placeholder: 'Téléphone *'
                                            }}
                                            preferredCountries={['qa', 'ir', 'iq', 'af', 'pk', 'sy', 'fr', 'us']}
                                            enableSearch={true}
                                            searchPlaceholder="Rechercher un pays"
                                            enableAreaCodes={false}
                                            enableLongNumbers={true}
                                            autoFormat={true}
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <textarea
                                        name="message"
                                        className="form-textarea"
                                        value={appointmentData.message}
                                        onChange={handleInputChange}
                                        placeholder="Message (précisez votre demande si nécessaire)"
                                    />
                                </div>

                                {/* Container reCAPTCHA avec référence */}
                                <div className="recaptcha-container">
                                    {!isRecaptchaLoaded ? (
                                        <div className="recaptcha-loading">
                                            Chargement du reCAPTCHA...
                                        </div>
                                    ) : (
                                        <div 
                                            ref={recaptchaRef}
                                            className="g-recaptcha"
                                            style={{ display: 'flex', justifyContent: 'center' }}
                                        ></div>
                                    )}
                                </div>
                                
                                <button
                                    type="submit"
                                    className="appointment-submit-btn"
                                    disabled={
                                        isSubmitting ||
                                        !recaptchaToken ||
                                        !appointmentData.nom.trim() || 
                                        !appointmentData.prenom.trim() || 
                                        !appointmentData.email.trim() || 
                                        !appointmentData.phone.trim() || 
                                        !appointmentData.objet || 
                                        !appointmentData.pays ||
                                        (appointmentData.objet === "Autres préoccupations" && !appointmentData.autresPreoccupation.trim())
                                    }
                                >
                                    {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Navbar;